import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error("Reset link is missing a token. Request a new one.");
      return;
    }
    if (pwd.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    if (pwd !== confirm) {
      toast.error("Passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: pwd });
      toast.success("Password updated successfully. Please log in with your new password.");
      nav("/login");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Unable to reset password.");
    } finally {
      setBusy(false);
    }
  };

  if (!token) {
    return (
      <div className="mx-auto max-w-md px-4 py-16" data-testid="reset-invalid-page">
        <h1 className="font-display text-4xl mb-2">Invalid link</h1>
        <p className="text-gray-600 mb-6">
          This reset link is missing or malformed. Please request a new one.
        </p>
        <Link
          to="/forgot-password"
          className="inline-block rounded-full bg-[#F48E2E] hover:bg-[#D97815] text-white px-6 py-3 text-sm font-medium"
          data-testid="reset-request-new"
        >
          Request a new link
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-md px-4 py-16" data-testid="reset-password-page">
      <h1 className="font-display text-4xl mb-2">Set a new password</h1>
      <p className="text-gray-600 mb-8">
        Choose a strong password with at least 8 characters. You will use this to log in from now on.
      </p>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label>New password</Label>
          <div className="relative">
            <Input
              data-testid="reset-password-new"
              type={showPwd ? "text" : "password"}
              required
              minLength={8}
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              className="pr-10"
            />
            <button
              type="button"
              data-testid="reset-password-new-toggle"
              onClick={() => setShowPwd((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              aria-label={showPwd ? "Hide password" : "Show password"}
              tabIndex={-1}
            >
              {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>
        <div>
          <Label>Confirm new password</Label>
          <div className="relative">
            <Input
              data-testid="reset-password-confirm"
              type={showConfirm ? "text" : "password"}
              required
              minLength={8}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="pr-10"
            />
            <button
              type="button"
              data-testid="reset-password-confirm-toggle"
              onClick={() => setShowConfirm((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              aria-label={showConfirm ? "Hide password" : "Show password"}
              tabIndex={-1}
            >
              {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>
        <Button
          data-testid="reset-submit"
          type="submit"
          disabled={busy}
          className="w-full rounded-full h-11 bg-[#F48E2E] hover:bg-[#D97815]"
        >
          {busy ? "Updating…" : "Update password"}
        </Button>
      </form>
      <div className="mt-6 text-sm text-center text-gray-600">
        <Link to="/login" className="text-[#F48E2E]" data-testid="reset-back-to-login">
          Back to login
        </Link>
      </div>
    </div>
  );
}
